/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';

import { TBrand } from '@global-types/api/api';
import {
	TQueryParams,
	TSortStatus,
} from '@global-types/index';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IBrandSlice {
	brands: TBrand[];
	brandsTotal: number;
	brandsPage: number;
	brandsPageSize: number;
	brandsTotalPages: number;
	brandsSelectedRecords: TBrand[];
	brandsQueryParams: TQueryParams;
	brandsSelectedRow: Partial<TBrand>;
	brandsSortParams: string;
	brandOrganization: string | null;
	brandsSortStatus: {};
	brandsImportErrors: string | null;
	brandsLastAccessTokenCreated: string | null;
	brandsOpenConfirmationModalDelete: {
		open: boolean;
		params: string;
	};
	brandsOpenConfirmationModalVerify: {
		open: boolean;
		params: string;
	};
	brandsOpenConfirmationModalMint: {
		open: boolean;
		params: string;
	};

	setBrandsLastAccessTokenCreated: (
		_brandsLastAccessTokenCreated: string
	) => void;

	setBrandsImportErrors: (_errors: string) => void;

	setBrandOrganization: (
		_organizationId: string | null
	) => void;

	setBrandsPage: (_brandsPage: number) => void;

	setBrandsSelectedRecords: (
		_brandsSelectedRecords: TBrand[]
	) => void;

	setBrandsOpenConfirmationModalDelete: (_brandsOpenConfirmationModalDelete: {
		open: boolean;
		params: any;
	}) => void;

	setBrandsOpenConfirmationModalVerify: (_brandsOpenConfirmationModalVerify: {
		open: boolean;
		params: any;
	}) => void;
	setBrandsOpenConfirmationModalMint: (_brandsOpenConfirmationModalMint: {
		open: boolean;
		params: any;
	}) => void;

	setBrandsQueryParams: (
		_brandsQueryParams: TQueryParams
	) => void;

	setBrandsSortStatus: (
		_brandsSortStatus: TSortStatus
	) => void;
	setBrandsSortParams: (
		_brandsSortParams: string
	) => void;

	setBrandsSelectedRow: (_brand: any) => void;
	setBrandsPageSize: (_pageSize: number) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	brands: [],
	brandsTotal: 0,
	brandsPage: 1,
	brandsPageSize: 100,
	brandsTotalPages: 0,
	brandsSelectedRecords: [],
	brandsQueryParams: {},
	brandsSelectedRow: {},
	brandsSortParams: '-createdAt',
	brandOrganization: null,
	brandsSortStatus: {},
	brandsImportErrors: null,
	brandsLastAccessTokenCreated: null,
	brandsOpenConfirmationModalDelete: {
		open: false,
		params: '',
	},
	brandsOpenConfirmationModalVerify: {
		open: false,
		params: '',
	},
	brandsOpenConfirmationModalMint: {
		open: false,
		params: '',
	},
};

export const createBrandsSlice: StateCreator<
	TStore,
	[],
	[],
	IBrandSlice
> = (set) => ({
	...initialState,
	setBrandOrganization: (
		_organizationId: string | null
	) => set({ brandOrganization: _organizationId }),

	setBrandsLastAccessTokenCreated: (
		_brandsLastAccessTokenCreated: string
	) =>
		set({
			brandsLastAccessTokenCreated:
				_brandsLastAccessTokenCreated,
		}),

	setBrandsImportErrors: (_errors: string) =>
		set({ brandsImportErrors: _errors }),

	setBrandsSelectedRow: (_brand: TBrand) =>
		set({ brandsSelectedRow: _brand }),

	setBrandsSortParams: (_sortStatus: string) =>
		set({ brandsSortParams: _sortStatus }),

	setBrandsSortStatus: (_sortStatus: TSortStatus) =>
		set({ brandsSortStatus: _sortStatus }),

	setBrandsQueryParams: (_queryParams: TQueryParams) =>
		set({ brandsQueryParams: _queryParams }),
	setBrandsTotal: (_total: number) =>
		set({ brandsTotal: _total }),
	setBrandsOpenConfirmationModalDelete:
		(_brandsOpenConfirmationModalDelete: {
			open: boolean;
			params: any;
		}) =>
			set({
				brandsOpenConfirmationModalDelete:
					_brandsOpenConfirmationModalDelete,
			}),
	setBrandsOpenConfirmationModalMint:
		(_brandsOpenConfirmationModalMint: {
			open: boolean;
			params: string;
		}) =>
			set({
				brandsOpenConfirmationModalMint:
					_brandsOpenConfirmationModalMint,
			}),
	setBrandsOpenConfirmationModalVerify:
		(_brandsOpenConfirmationModalVerify: {
			open: boolean;
			params: string;
		}) =>
			set({
				brandsOpenConfirmationModalVerify:
					_brandsOpenConfirmationModalVerify,
			}),
	setBrandsPage: (_page: number) =>
		set({ brandsPage: _page }),
	setBrandsPageSize: (_pageSize: number) =>
		set({ brandsPageSize: _pageSize }),
	setBrandsTotalPages: (_totalPages: number) =>
		set({ brandsTotalPages: _totalPages }),
	setBrandsSelectedRecords: (
		_brandsSelectedRecords: any[]
	) =>
		set({
			brandsSelectedRecords: _brandsSelectedRecords,
		}),
});
